"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState, useRef, useEffect } from "react";
import { Mail, Phone, MapPin, Calendar, Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(0);
  const [hoverGlow, setHoverGlow] = useState(false);
  const {
    ref: titleRef,
    isVisible: titleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: subtitleRef,
    isVisible: subtitleVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: mapsRef,
    isVisible: mapsVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  const parallaxOffset = scrollY * 0.5;
  const locations = [{
    city: "Lagos",
    address: "Lagos, Nigeria",
    mapImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/lagos_map_location.png"
  }, {
    city: "Abuja",
    address: "Abuja, Nigeria",
    mapImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/abuja_map_location.png"
  }, {
    city: "Port Harcourt",
    address: "Port Harcourt, Nigeria",
    mapImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/port_harcourt_map_location.png"
  }];
  return <section className="relative min-h-screen overflow-hidden bg-background">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1522495/img/lagos_skyline_hero_background.png)]" style={{
        transform: `translateY(${parallaxOffset}px)`
      }} />
        <div className="absolute inset-0 bg-dark-background/70" />
      </div>

      {}
      <div className="relative z-10 container mx-auto px-4 pt-32 pb-20">
        <div className="text-center max-w-4xl mx-auto" onMouseEnter={() => setHoverGlow(true)} onMouseLeave={() => setHoverGlow(false)}>
          <h1 ref={titleRef} className={`text-5xl md:text-7xl font-display font-bold text-foreground-dark mb-6 transition-all duration-[var(--duration-medium)] ${titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${hoverGlow ? "drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]" : ""}`}>
            Get in Touch with Nethill Limited
          </h1>
          <p ref={subtitleRef} className={`text-xl md:text-2xl text-foreground-dark/90 transition-all duration-[var(--duration-medium)] delay-100 ${subtitleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${hoverGlow ? "drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]" : ""}`}>
            Your partner in Nigerian real estate and digital excellence
          </p>
        </div>
      </div>

      {}
      <div className="relative z-10 container mx-auto px-4 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {}
          <div ref={formRef} className={`transition-all duration-[var(--duration-slow)] ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Card className="glass-effect">
              <CardHeader>
                <CardTitle className="text-3xl text-foreground">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <FormProvider>
                  <form className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                        Full Name
                      </label>
                      <input type="text" id="name" name="name" required className="w-full px-4 py-3 rounded-md bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-[var(--duration-fast)]" placeholder="John Doe" />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required className="w-full px-4 py-3 rounded-md bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-[var(--duration-fast)]" placeholder="john@example.com" />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" name="phone" className="w-full px-4 py-3 rounded-md bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-[var(--duration-fast)]" placeholder="+234 800 123 4567" />
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-foreground mb-2">
                        Service Interest
                      </label>
                      <select id="service" name="service" required className="w-full px-4 py-3 rounded-md bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-[var(--duration-fast)]">
                        <option value="">Select a service</option>
                        <option value="property">Property Sourcing</option>
                        <option value="investment">Investment Analysis</option>
                        <option value="portfolio">Portfolio Management</option>
                        <option value="seo">SEO Audits</option>
                        <option value="google">Google Business Profile Optimization</option>
                        <option value="website">Website SEO Score Checker</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                        Message
                      </label>
                      <textarea id="message" name="message" required rows={5} className="w-full px-4 py-3 rounded-md bg-muted border border-border text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-[var(--duration-fast)] resize-none" placeholder="Tell us about your needs..." />
                    </div>

                    <button type="submit" className="w-full bg-secondary text-foreground-secondary px-6 py-4 rounded-md font-semibold hover:bg-secondary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-secondary-active active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </button>
                  </form>
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className={`space-y-6 transition-all duration-[var(--duration-slow)] delay-100 ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <Card className="glass-effect">
              <CardHeader>
                <CardTitle className="text-3xl text-foreground">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                    <p className="text-muted-foreground">+234 800 123 4567</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Email</h3>
                    <p className="text-muted-foreground">info@nethill.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Hours</h3>
                    <p className="text-muted-foreground">Mon-Fri: 9am-6pm WAT</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <SafeHydrate><Calendar className="w-6 h-6 text-primary" /></SafeHydrate>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">Book a Consultation</h3>
                    <p className="text-muted-foreground mb-3">
                      Schedule a meeting with our experts
                    </p>
                    <Link to="https://calendly.com" newTab className="inline-flex items-center gap-2 bg-primary text-foreground-primary px-6 py-3 rounded-md font-semibold hover:bg-primary-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-primary-active active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                      <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
                      Schedule Now
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {}
      <div className="relative z-10 bg-muted py-20">
        <div className="container mx-auto px-4">
          <div ref={mapsRef} className={`text-center mb-12 transition-all duration-[var(--duration-medium)] ${mapsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              Our Service Areas
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We serve major cities across Nigeria with our real estate and digital services
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {locations.map((location, i) => <div key={location.city} data-index={i} className={`transition-all duration-[var(--duration-slow)] ${mapsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${i * 100}ms`
          }}>
                <Card className="hover-lift overflow-hidden">
                  <div className="aspect-[4/3] bg-accent overflow-hidden">
                    <img src={location.mapImage} alt={`${location.city} location map`} className="w-full h-full object-cover" />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-2xl text-foreground flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-primary" />
                      {location.city}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{location.address}</p>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}